import React, { useState, useEffect } from 'react';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	AreaChart,
	Area,
} from 'recharts';
import TableLoading from '../../components/TableLoading';
import { formatCamelCaseToText, formatHyphenatedText, mapTimeRangeToApiFilterAlt3, request } from '../../services/utilities';
import { analyticsAPI, analyticsAPIV2 } from '../../services/constants';
import moment from 'moment';
import { TimeRangeDropdownAlt3 } from '../../components/TimeRangeDropdownAlt3';
import { Statistic } from 'antd';

const AntenatalOverview = () => {
	const [timeRanges, setTimeRanges] = useState({
		assessmentTrends: 'past30Days',
		assessmentCount: 'past30Days',
		assessmentByDoctors: 'past30Days',
		enrollmentHistory: 'past30Days',
		enrollmentDoctors: 'past30Days',
		enrollmentStatus: 'past30Days',
		enrollmentPackage: 'past30Days',
		enrollmentImpSource: 'past30Days',
		enrollmentBooking: 'past30Days',
		enrollmentTrends: 'past30Days',
		enrollmentCount: 'past30Days',
		metrics: 'past30Days',
		completedGestationByAge: 'past30Days',
		dashboard: 'past30Days',
		ancRank: 'past30Days',
		pregnancy: 'past30Days',
	});

	const [loadingStates, setLoadingStates] = useState({
		assessmentTrends: false,
		assessmentCount: false,
		assessmentByDoctors: false,
		enrollmentHistory: false,
		enrollmentDoctors: false,
		enrollmentStatus: false,
		enrollmentPackage: false,
		enrollmentImpSource: false,
		enrollmentBooking: false,
		enrollmentTrends: false,
		enrollmentCount: false,
		metrics: false,
		completedGestationByAge: false,
		dashboard: false,
		ancRank: false,
		pregnancy: false,
	});

	const [assessmentTrends, setAssessmentTrends] = useState([]);
	const [assessmentCount, setAssessmentCount] = useState(0);
	const [assessmentByDoctors, setAssessmentByDoctors] = useState([]);
	const [enrollmentHistory, setEnrollmentHistory] = useState([]);
	const [enrollmentDoctors, setEnrollmentDoctors] = useState([]);
	const [enrollmentStatus, setEnrollmentStatus] = useState([]);
	const [enrollmentPackage, setEnrollmentPackage] = useState([]);
	const [enrollmentImpSource, setEnrollmentImpSource] = useState([]);
	const [enrollmentBooking, setEnrollmentBooking] = useState([]);
	const [enrollmentTrends, setEnrollmentTrends] = useState([]);
	const [enrollmentCount, setEnrollmentCount] = useState(0);
	const [completedGestationByAge, setCompletedGestationByAge] = useState([]);
	const [dashboard, setDashboard] = useState([]);
	const [ancRank, setAncRank] = useState([]);
	const [pregnacy, setPregnancy] = useState([]);

	const fetchAssessmentTrends = async range => {
		setLoadingStates(prev => ({ ...prev, assessmentTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-assessment-trends?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				count: parseInt(x?.count, 10) || 0,
			}));
			setAssessmentTrends(apiResponse);
		} catch (error) {
			console.error('Failed to fetch assessmentTrends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, assessmentTrends: false }));
		}
	};

	const fetchAssessmentCount = async range => {
		setLoadingStates(prev => ({ ...prev, assessmentCount: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-assessment-count?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = parseInt(res?.result);
			setAssessmentCount(apiResponse);
		} catch (error) {
			console.error('Failed to fetch assessmentCount:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, assessmentCount: false }));
		}
	};

	const fetchAssessmentByDoctors = async range => {
		setLoadingStates(prev => ({ ...prev, assessmentByDoctors: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-assessment-by-doctor?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				?.sort((a, b) => b.total_value - a.total_value)
				?.map(x => ({
					name: x?.doctor || 'Unknown',
					count: parseInt(x?.count, 10),
				}))
				.slice(0, 30);
			setAssessmentByDoctors(apiResponse);
		} catch (error) {
			console.error('Failed to fetch assessmentByDoctors:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, assessmentByDoctors: false }));
		}
	};

	const fetchEnrollmentHistory = async (range, chartId) => {
		setLoadingStates(prev => ({ ...prev, enrollmentHistory: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-history?dateFilter=${dateFilter}`, 'GET', true);
			setEnrollmentHistory(res?.result || []);
		} catch (error) {
			console.error('Failed to fetch enrollmentHistory:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentHistory: false }));
		}
	};

	const fetchEnrollmentDoctors = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentDoctors: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-doctors?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse =
				res?.result?.map(x => ({
					name: x?.doctor_name,
					count: parseInt(x?.count, 10) || 0,
				})) || [];
			setEnrollmentDoctors(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentDoctors:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentDoctors: false }));
		}
	};

	const fetchEnrollmentStatus = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentStatus: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-status?dateFilter=${dateFilter}`, 'GET', true);
			setEnrollmentStatus(res?.result || []);
		} catch (error) {
			console.error('Failed to fetch enrollmentStatus:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentStatus: false }));
		}
	};

	const fetchEnrollmentPackage = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentPackage: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-package?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse =
				res?.result?.map(x => ({
					name: x?.package_name,
					count: parseInt(x?.count, 10) || 0,
				})) || [];
			setEnrollmentPackage(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentPackage:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentPackage: false }));
		}
	};

	const fetchEnrollmentImpSource = async (range, chartId) => {
		setLoadingStates(prev => ({ ...prev, enrollmentImpSource: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-lmp-source?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse =
				res?.result?.map(x => ({
					name: x?.lmp_source || 'Unknown',
					count: parseInt(x?.count, 10) || 0,
				})) || [];
			setEnrollmentImpSource(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentImpSource:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentImpSource: false }));
		}
	};

	const fetchEnrollmentBooking = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentBooking: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-booking?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse =
				res?.result?.map(x => ({
					name: x?.booking_period || 'Unknown',
					count: parseInt(x?.count, 10) || 0,
				})) || [];
			setEnrollmentBooking(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentBooking:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentBooking: false }));
		}
	};

	const fetchEnrollmentTrends = async (range, chartId) => {
		setLoadingStates(prev => ({ ...prev, enrollmentTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-trends?dateFilter=${dateFilter}`, 'GET', true);
			const data =
				res?.result?.map(x => ({
					name: x?.date || x?.day || x?.week || x?.month || x?.year,
					count: parseInt(x?.count, 10) || 0,
				})) || [];
			setEnrollmentTrends(data);
		} catch (error) {
			console.error('Failed to fetch enrollmentTrends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentTrends: false }));
		}
	};

	const fetchEnrollmentCount = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentCount: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/antenatal-enrollment-count?dateFilter=${dateFilter}`, 'GET', true);
			setEnrollmentCount(res?.result || 0);
		} catch (error) {
			console.error('Failed to fetch enrollmentCount:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentCount: false }));
		}
	};

	const fetchCompleteGestationByAge = async range => {
		setLoadingStates(prev => ({ ...prev, completedGestationByAge: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPIV2}/anc/completed-by-gestation-age?dateFilter=${dateFilter}`, 'GET', true);
			setCompletedGestationByAge(res);
		} catch (error) {
			console.error('Failed to fetch completedGestationByAge:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, completedGestationByAge: false }));
		}
	};

	// const fetchDashboard = async range => {
	// 	setLoadingStates(prev => ({ ...prev, dashboard: true, metrics:true }));
	// 	const dateFilter = mapTimeRangeToApiFilterAlt3(range);
	// 	try {
	// 		const res = await request(`${analyticsAPIV2}/anc/dashboard?dateFilter=${dateFilter}`, 'GET', true);
	// 		setDashboard(res);
	// 	} catch (error) {
	// 		console.error('Failed to fetch completedGestationByAge:', error);
	// 	} finally {
	// 		setLoadingStates(prev => ({ ...prev, dashboard: false, metrics:false }));
	// 	}
	// };

	const fetchDashboard = async range => {
		setLoadingStates(prev => ({ ...prev, dashboard: true, metrics: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);

		try {
			const res = await request(`${analyticsAPIV2}/anc/dashboard?dateFilter=${dateFilter}`, 'GET', true);

			// 🟦 Overview
			const overview = {
				name: 'Overview',
				data: Object.entries(res.overview || {}).map(([key, value]) => ({
					name: key.charAt(0).toUpperCase() + key.slice(1),
					value,
				})),
			};

			// 🟩 By Package
			const byPackage = (res.byPackage || []).map(pkg => ({
				name: pkg.packageName,
				data: Object.entries(pkg)
					.filter(([k]) => ['total', 'active', 'completed'].includes(k))
					.map(([key, value]) => ({
						name: key.charAt(0).toUpperCase() + key.slice(1),
						value,
					})),
			}));

			// 🟨 Assessments
			const assessments = {
				name: 'Assessments',
				data: Object.entries(res.assessments || {}).map(([key, value]) => ({
					name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
					value,
				})),
			};

			// 🧩 Combine all into one state
			const unifiedData = [overview, ...byPackage, assessments];

			setDashboard(unifiedData); // 👈 single state
			console.log(unifiedData, 'unifified data---------------------');
		} catch (error) {
			console.error('Failed to fetch dashboard data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, dashboard: false, metrics: false }));
		}
	};

	const fetchAncByDoctors = async range => {
		setLoadingStates(prev => ({ ...prev, ancRank: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPIV2}/anc/by-doctor?dateFilter=${dateFilter}`, 'GET', true);

			const mappedData = res?.map(x => ({
				Rank: x?.rank,
				Name: x?.doctorName,
				Email: x?.email,
				'Phone Numer': x?.phoneNumber,
				Active: x?.active,
				Completed: x?.completed,
				Total: x?.total,
			}));
			setAncRank(mappedData);
		} catch (error) {
			console.error('Failed to fetch anc rank by doctors:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, ancRank: false }));
		}
	};

	const fetchPregnancyStats = async range => {
		setLoadingStates(prev => ({ ...prev, pregnancy: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPIV2}/anc/pregnancy-stats?dateFilter=${dateFilter}`, 'GET', true);

			const mappedData = res?.map(x => ({
				Gravida: x?.gravida,
				count: x?.count,
			}));
			setPregnancy(mappedData);
		} catch (error) {
			console.error('Failed to fetch anc rank by doctors:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, pregnancy: false }));
		}
	};

	useEffect(() => {
		fetchAssessmentCount(timeRanges.assessmentTrends);
		fetchEnrollmentCount(timeRanges.enrollmentCount);
		fetchAssessmentTrends(timeRanges.assessmentTrends);
		fetchAssessmentByDoctors(timeRanges.assessmentByDoctors);
		fetchEnrollmentHistory(timeRanges.enrollmentHistory);
		fetchEnrollmentDoctors(timeRanges.enrollmentDoctors);
		fetchEnrollmentStatus(timeRanges.enrollmentStatus);
		fetchEnrollmentPackage(timeRanges.enrollmentPackage);
		fetchEnrollmentImpSource(timeRanges.enrollmentImpSource);
		fetchEnrollmentBooking(timeRanges.enrollmentBooking);
		fetchEnrollmentTrends(timeRanges.enrollmentTrends);
		fetchCompleteGestationByAge(timeRanges.completedGestationByAge);
		fetchDashboard(timeRanges.dashboard);
		fetchAncByDoctors(timeRanges.ancRank);
		fetchPregnancyStats(timeRanges.pregnancy);
	}, []);

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'metrics':
				// fetchAssessmentCount(range);
				// fetchEnrollmentCount(range);
				fetchDashboard(range);
				break;
			case 'assessmentTrends':
				fetchAssessmentTrends(range);
				break;
			case 'assessmentByDoctors':
				fetchAssessmentByDoctors(range);
				break;
			case 'enrollmentHistory':
				fetchEnrollmentHistory(range);
				break;
			case 'enrollmentDoctors':
				fetchEnrollmentDoctors(range);
				break;
			case 'enrollmentStatus':
				fetchEnrollmentStatus(range);
				break;
			case 'enrollmentPackage':
				fetchEnrollmentPackage(range);
				break;
			case 'enrollmentImpSource':
				fetchEnrollmentImpSource(range);
				break;
			case 'enrollmentBooking':
				fetchEnrollmentBooking(range);
				break;
			case 'enrollmentTrends':
				fetchEnrollmentTrends(range);
				break;
			case 'completedGestationByAge':
				fetchCompleteGestationByAge(range);
				break;
			case 'dashboard':
				fetchDashboard(range);
				break;
			case 'ancRank':
				fetchAncByDoctors(range);
				break;
			case 'pregnacy':
				fetchPregnancyStats(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	const MetricsConfig = [
		{
			key: 'assessmentCount',
			label: 'Assessment Count',
			color: '#1890ff',
			value: assessmentCount,
		},
		{
			key: 'enrollmentCount',
			label: 'enrollmentCount',
			color: '#52c41a',
			value: enrollmentCount,
		},
	];

	const MetricsCard = ({ value, label, color, loading }) => {
		const safeValue = Number(value) || 0;

		return (
			<div className="col-sm-4">
				<a className="element-box el-tablo" href="#">
					<Statistic
						title={formatCamelCaseToText(label)}
						value={loading ? <TableLoading /> : safeValue.toLocaleString()}
						valueStyle={{ fontWeight: 'bold', color }}
					/>
					<small className="text-muted">{formatCamelCaseToText(label)} for </small>
					<small className="text-muted">
						<strong>{formatHyphenatedText(mapTimeRangeToApiFilterAlt3(timeRanges?.metrics))}</strong>
					</small>
				</a>
			</div>
		);
	};

	return (
		<div className="content-">
			<div className="content-bo">
				<div className="row">
					{/* Metrics Overview */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Antenatal Overview </h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.metrics} onChange={handleTimeRangeChange} chartId="metrics" />
							</div>
							<p className="chart-description card p-2">Summary metrics showing overall Antenatal activity.</p>

							<div className="element-box">
								<div className="w-100">
									{loadingStates?.metrics ? (
										<TableLoading />
									) : (
										<div className=" w-100">
											{/* {MetricsConfig.map(({ key, label, color, value }) => (
												<MetricsCard key={key} label={label} color={color} value={value} loading={loadingStates?.metrics} />
											))} */}

											{dashboard?.map((x, idx) => {
												// Find the total value for this accommodation
												const totalItem = x?.data.find(item => item.name.toLowerCase().includes('total'));
												const totalValue = Number(totalItem?.value) || 0;
												const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc0cb', '#faad14'];

												return (
													<div key={idx} className=" 1-100">
														<h6 className="pl-3 mt-2">{x?.name || 'Not Assigned'}</h6>

														<div className="row">
															{x?.data?.map((item, i) => {
																const color = COLORS[i % COLORS.length];
																const percentage =
																	item.name.toLowerCase().includes('total') || totalValue === 0
																		? null
																		: ((item.value / totalValue) * 100).toFixed(1); // e.g. 45.2%

																return (
																	<div key={i} className="col-4 mb-3">
																		<a className="element-box el-tablo" href="#">
																			<Statistic
																				title={formatCamelCaseToText(item?.name)}
																				value={item?.value}
																				valueStyle={{
																					fontWeight: 'bold',
																					color: color,
																				}}
																			/>

																			{!item?.name.toLowerCase().includes('total') && (
																				<small className="text-muted bold">
																					<strong style={{ color }}>{percentage}%</strong> of Total for &nbsp;
																				</small>
																			)}

																			<small className="text-muted">
																				<strong>{formatHyphenatedText(mapTimeRangeToApiFilterAlt3(timeRanges?.count))}</strong>
																			</small>
																		</a>
																	</div>
																);
															})}
														</div>
													</div>
												);
											})}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Assessment Trends */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Assessment Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.assessmentTrends} onChange={handleTimeRangeChange} chartId="assessmentTrends" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Tracks changes in antenatal assessments over time to identify patterns and growth
							</p>

							<div className="element-box">
								{loadingStates.assessmentTrends ? (
									<TableLoading />
								) : assessmentTrends?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={assessmentTrends} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.assessmentTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#FFBB28"
													fill="#FFBB28"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Assessment By Doctors */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Assessment By Doctors</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges?.assessmentByDoctors}
									onChange={handleTimeRangeChange}
									chartId="assessmentByDoctors"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">
								Tracks changes in antenatal assessments over time to identify patterns and growth
							</p>

							<div className="element-box">
								{loadingStates.assessmentByDoctors ? (
									<TableLoading />
								) : assessmentByDoctors?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={assessmentByDoctors} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#FFBB28"
													fill="#FFBB28"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Assessment By Doctors */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Enrollment Doctors</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.enrollmentDoctors} onChange={handleTimeRangeChange} chartId="enrollmentDoctors" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Visualizes antenatal enrollments grouped by the doctors responsible for the registrations
							</p>

							<div className="element-box">
								{loadingStates.enrollmentDoctors ? (
									<TableLoading />
								) : enrollmentDoctors?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={enrollmentDoctors} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#ccBB28"
													fill="#ccBB28"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Antenatal Enrolment Package */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Enrollment Package</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.enrollmentPackage} onChange={handleTimeRangeChange} chartId="enrollmentPackage" />
							</div>
							<p className="chart-description mb-3 p-2 card">Displays the distribution of antenatal enrollment by package type or plan.</p>

							<div className="element-box">
								{loadingStates.enrollmentPackage ? (
									<TableLoading />
								) : enrollmentPackage?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={enrollmentPackage} margin={{ top: 80, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="count" fill="#0088FE">
													<LabelList dataKey="count" position="top" content={formatBarLabel} style={{ fontSize: '12px', fill: '#333' }} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Antenatal Enrolment Imp Source */}
					<div className="col-sm-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Enrollment LMP Source</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges?.enrollmentImpSource}
									onChange={handleTimeRangeChange}
									chartId="enrollmentImpSource"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">
								Highlights the sources from which antenatal patients were referred or influenced.
							</p>

							<div className="element-box">
								{loadingStates.enrollmentImpSource ? (
									<TableLoading />
								) : enrollmentImpSource?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={enrollmentImpSource} margin={{ top: 80, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Bar dataKey="count" fill="#0088FE">
													<LabelList dataKey="count" position="top" content={formatBarLabel} style={{ fontSize: '12px', fill: '#333' }} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Antenatal Enrolment Bookings */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Enrollment Bookings</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.enrollmentBooking} onChange={handleTimeRangeChange} chartId="enrollmentBooking" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Presents data on how antenatal bookings are scheduled across dates or times.
							</p>

							<div className="element-box">
								{loadingStates.enrollmentBooking ? (
									<TableLoading />
								) : enrollmentBooking?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={enrollmentBooking} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend />
												<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
													<LabelList
														dataKey="count"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '10px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Antenatal Enrolment Trends */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal Enrollment Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.enrollmentTrends} onChange={handleTimeRangeChange} chartId="enrollmentTrends" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Shows trends in antenatal enrollments over a time period to monitor growth or drop.
							</p>

							<div className="element-box">
								{loadingStates.enrollmentTrends ? (
									<TableLoading />
								) : enrollmentTrends?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={enrollmentTrends} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.enrollmentTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="count" stroke="#22BB33" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
													<LabelList
														dataKey="count"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '10px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* completed gestations by age */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Completed Gestattion By Age</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges?.completedGestationByAge}
									onChange={handleTimeRangeChange}
									chartId="completedGestationByAge"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart shows the gestational age of pregnacy for the selected time range.
							</p>

							<div className="element-box">
								{loadingStates.completedGestationByAge ? (
									<TableLoading />
								) : completedGestationByAge?.byGestationAge?.length > 0 ? (
									<div style={{ height: '500px' }}>
										<ResponsiveContainer>
											<BarChart data={completedGestationByAge.byGestationAge} margin={{ top: 80, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'gestationRange'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													content={({ active, payload, label }) => {
														if (!active || !payload || !payload.length) return null;
														const datum = (payload[0] && payload[0].payload) || {};
														const gestation = datum.gestationRange || label || '';
														const countValue = Number(datum.count ?? payload[0].value ?? 0);
														let percentage = null;
														if (datum.percentage != null) {
															const pct = Number(datum.percentage);
															percentage = pct <= 1 ? (pct * 100).toFixed(2) + '%' : pct.toFixed(2) + '%';
														} else if (datum.percent != null) {
															const pct = Number(datum.percent);
															percentage = pct <= 1 ? (pct * 100).toFixed(2) + '%' : pct.toFixed(2) + '%';
														}
														return (
															<div className="card p-2" style={{ minWidth: 160 }}>
																<div style={{ marginBottom: 6 }}>
																	<strong>Gestation:</strong> {String(gestation)}
																</div>
																<div style={{ marginBottom: 6 }}>
																	<strong>Count:</strong> {countValue.toLocaleString()}
																</div>
																{percentage && (
																	<div>
																		<strong>Percentage:</strong> {percentage}
																	</div>
																)}
															</div>
														);
													}}
												/>
												<Legend wrapperStyle={{ marginBottom: '-55px' }} />
												<Bar dataKey="count" fill="#0088FE">
													<LabelList
														dataKey="count"
														position="top"
														name=""
														content={formatBarLabel}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* antenatal enrolments by doctors/ rankings */}
					<div className="col-sm-12 col-lg-12">
						<div className="element-wrapper">
							<h6 className="element-header">Antenatal By Doctors</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.ancRank} onChange={handleTimeRangeChange} chartId="ancRank" />
							</div>
							<p className="chart-description mb-3 p-2 card">This chart ranks doctors antenatal engagements over a selected time period.</p>

							<div className="element-box">
								{loadingStates.ancRank ? (
									<TableLoading />
								) : ancRank?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={ancRank} margin={{ top: 80, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'Name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													content={({ active, payload, label }) => {
														if (!active || !payload || !payload.length) return null;
														const data = payload[0].payload;
														return (
															<div className="card p-2" style={{ minWidth: 200 }}>
																<div style={{ marginBottom: 6 }}>
																	<strong>Rank:</strong> {data.Rank}
																</div>

																<div style={{ marginBottom: 6 }}>
																	<strong>Name:</strong> {data.Name}
																</div>
																<div style={{ marginBottom: 6 }}>
																	<strong>Phone Number:</strong> {data['Phone Numer']}
																</div>
																<div style={{ marginBottom: 6 }}>
																	<strong>Email:</strong> {data.Email}
																</div>
																<div style={{ marginBottom: 6 }}>
																	<strong>Total:</strong> {data.Total?.toLocaleString()}
																</div>
																<div style={{ marginBottom: 6 }}>
																	<strong>Active:</strong> {data.Active?.toLocaleString()}
																</div>
																<div>
																	<strong>Completed:</strong> {data.Completed?.toLocaleString()}
																</div>
															</div>
														);
													}}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="Total" fill="#0088FE">
													<LabelList dataKey="Total" position="top" content={formatBarLabel} style={{ fontSize: '12px', fill: '#333' }} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* pregnancy stats */}
					<div className="col-sm-12 col-lg-12">
						<div className="element-wrapper">
							<h6 className="element-header">Pregnancy Stats</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.pregnancy} onChange={handleTimeRangeChange} chartId="pregnacy" />
							</div>
							<p className="chart-description mb-3 p-2 card">This chart shows pregnacy stats.</p>

							<div className="element-box">
								{loadingStates.pregnancy ? (
									<TableLoading />
								) : pregnacy?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={pregnacy} margin={{ top: 80, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'Gravida'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													content={({ active, payload, label }) => {
														if (!active || !payload || !payload.length) return null;
														const data = payload[0].payload;
														return (
															<div className="card p-2" style={{ minWidth: 200 }}>
																<div style={{ marginBottom: 6 }}>
																	<strong>Gravida:</strong> {data.Gravida}
																</div>

																<div style={{ marginBottom: 6 }}>
																	<strong>Count:</strong> {data.count}
																</div>
															</div>
														);
													}}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="count" fill="#0088FE">
													<LabelList dataKey="count" position="top" content={formatBarLabel} style={{ fontSize: '12px', fill: '#333' }} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AntenatalOverview;
